import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import Button from 'src/components/common/Button';
import Icon from 'src/components/common/Icon';
import Badge from 'src/components/common/Badge';
import Tooltip from 'src/components/common/Tooltip';
import Localize from 'src/components/common/Localize';
import useAiChat from 'src/hooks/useAiChat';
import { useIntlMessages } from 'src/hooks';

import './AiChatWidget.scss';

const AiChatWidget = ({ className }) => {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const intlMessages = useIntlMessages();
  const locale = process.env.REACT_APP_LANGUAGE;

  const {
    messages,
    isLoading,
    hasError,
    errorMessage,
    isVisible,
    hasUnreadDailyMessage,
    lastDailyCheckDate,
    sendMessage,
    fetchDailyCheck,
    markDailyMessageAsRead,
    resetChat,
    toggleVisibility,
  } = useAiChat();

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleToggleVisibility = useCallback(() => {
    if (!isVisible && hasUnreadDailyMessage) {
      markDailyMessageAsRead();
    }
    toggleVisibility();
  }, [
    isVisible,
    hasUnreadDailyMessage,
    markDailyMessageAsRead,
    toggleVisibility,
  ]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (inputValue.trim() && !isLoading) {
        sendMessage(inputValue, { conversationHistory: messages, locale })
          .then(() => {
            setInputValue('');
          })
          .catch((error) => {
            console.error('Failed to send message:', error);
          });
      }
    },
    [inputValue, isLoading, sendMessage, messages, locale]
  );

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit]
  );

  const handleInputChange = useCallback((e) => {
    setInputValue(e.target.value);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    const today = new Date().toDateString();

    if (lastDailyCheckDate !== today) {
      fetchDailyCheck(locale).catch((error) => {
        console.error('Failed to fetch daily check:', error);
      });
    }
  }, [lastDailyCheckDate, fetchDailyCheck, locale]);

  return (
    <>
      {!isVisible && (
        <>
          <Tooltip id="aiChat">
            <Localize id="aiChatWidget.chat" />
          </Tooltip>
          <Badge
            className={cn('ai-chat-toggle', className, {
              'ai-chat-toggle--has-notification': hasUnreadDailyMessage,
            })}
            img="parrot"
            type="primary"
            onClick={handleToggleVisibility}
            tooltip="aiChat"
            count={hasUnreadDailyMessage ? '!' : null}
            aria-label={intlMessages.aiChatWidget.ariaOpenChat}
          />
        </>
      )}
      {isVisible && (
        <div
          className={`ai-chat-widget ${className || ''}`}
          role="dialog"
          aria-labelledby="ai-chat-title"
          aria-modal="false"
        >
          <div className="ai-chat-widget__header">
            <h3 id="ai-chat-title" className="ai-chat-widget__title">
              {intlMessages.aiChatWidget.title}
            </h3>
            <div className="ai-chat-widget__header-actions">
              <Button
                className="ai-chat-widget__clear"
                onClick={resetChat}
                variant="secondary"
                disabled={messages.length === 0}
                label={intlMessages.aiChatWidget.clear}
                aria-label={intlMessages.aiChatWidget.ariaClearChat}
                collapsed
              />
              <Button
                className="ai-chat-widget__close"
                onClick={handleToggleVisibility}
                variant="secondary"
                aria-label={intlMessages.aiChatWidget.ariaCloseChat}
                collapsed
              >
                <Icon name="close" size={16} color="black" />
              </Button>
            </div>
          </div>
          <div
            className="ai-chat-widget__messages"
            role="log"
            aria-live="polite"
            aria-relevant="additions"
          >
            {messages.length === 0 && (
              <div className="ai-chat-widget__empty">
                <p>{intlMessages.aiChatWidget.emptyMessage}</p>
              </div>
            )}
            {messages.map((message, index) => (
              <div
                key={index}
                className={`ai-chat-widget__message ai-chat-widget__message--${message.type}`}
                role="article"
                aria-label={`${
                  message.type === 'user'
                    ? intlMessages.aiChatWidget.ariaUserMessage
                    : intlMessages.aiChatWidget.ariaAiMessage
                } ${intlMessages.aiChatWidget.ariaMessageSuffix}`}
              >
                <div className="ai-chat-widget__message-content">
                  {message.text}
                </div>
              </div>
            ))}
            {hasError && (
              <div
                className="ai-chat-widget__error"
                role="alert"
                aria-live="assertive"
              >
                {errorMessage || intlMessages.aiChatWidget.errorMessage}
              </div>
            )}
            {isLoading && (
              <div
                className="ai-chat-widget__message ai-chat-widget__message--ai"
                role="status"
                aria-live="polite"
                aria-label={intlMessages.aiChatWidget.ariaTyping}
              >
                <div className="ai-chat-widget__message-content">
                  <div className="ai-chat-widget__loading" aria-hidden="true">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <form
            className="ai-chat-widget__input-form"
            onSubmit={handleSubmit}
            aria-label={intlMessages.aiChatWidget.ariaFormLabel}
          >
            <input
              type="text"
              className="ai-chat-widget__input"
              placeholder={intlMessages.aiChatWidget.inputPlaceholder}
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              aria-label={intlMessages.aiChatWidget.inputPlaceholder}
              aria-busy={isLoading}
            />
            <Button
              isSubmit
              className="ai-chat-widget__send-button"
              disabled={isLoading || !inputValue.trim()}
              label={intlMessages.aiChatWidget.send}
              variant="primary"
              aria-label={intlMessages.aiChatWidget.ariaSendMessage}
              collapsed
            />
          </form>
        </div>
      )}
    </>
  );
};

AiChatWidget.propTypes = {
  className: PropTypes.string,
};

AiChatWidget.defaultProps = {
  className: '',
};

export default AiChatWidget;
